import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCoop } from "../contexts/CoopContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { themeForFloor } from "../utils/themes";
import { loadChromaKeySprite } from "../utils/spriteLoader";
import { SFX, playMusic, trackForFloor } from "../utils/audio";
import { drawBiomeDecor } from "../utils/biomeDecor";
import { drawTileDecor } from "../utils/tileDecor";
import { drawBiomeTile } from "../utils/biomeFloor";
import {
  ROOM_W, ROOM_H, TILE_W, TILE_H, PLAYER_SPEED, DOOR_RADIUS, INTERACT_RADIUS,
  isoProject, DOOR_POS, drawWalls, drawDoor, drawDecoration, drawInteractable,
  drawPlayer, drawRemotePlayer,
} from "../utils/isoRender";
import { doorsForRoom, isRoomClearedFor, isCombatRoom } from "../utils/coopMap";
import { scaleEnemyStats, applyChestBonuses } from "../utils/gameLogic";
import { freshCombatExtras } from "../utils/coopCombatEngine";
import { getRandomEnemy, getBossForFloor } from "../data/enemies";
import { generateChestContent, rollChestRarity } from "../data/chests";
import { generateSanctuaryPassive, rollSanctuaryRarity } from "../data/passives";
import { ChestRewardView } from "../components/game/ChestRewardView";
import { SanctuaryChoiceView } from "../components/game/SanctuaryChoiceView";
import { generateShop } from "../data/shop";
import { StatsGrid } from "../components/game/StatsDisplay";
import { ITEMS, ITEM_RARITY_COLORS } from "../data/items";
import { toast } from "sonner";
import { LogOut, Skull, BarChart3, Backpack, X, Eye } from "lucide-react";
import CoopCombat from "./CoopCombat";
import CoopDuel from "./CoopDuel";

const F_DUEL_RADIUS = 1.6;

// Co-op enemy scaling : HP × N, dégâts × 1.2^(N-1)
const coopScale = (stats, n) => {
  if (!n || n <= 1) return stats;
  const dmg = Math.pow(1.2, n - 1);
  return {
    ...stats,
    pv: Math.max(1, Math.round(stats.pv * n)),
    pvMax: Math.max(1, Math.round((stats.pvMax || stats.pv) * n)),
    atk: Math.max(1, Math.round(stats.atk * dmg)),
    degatsElem: Math.max(0, Math.round((stats.degatsElem || 0) * dmg)),
  };
};

const buildEnemyForRoom = (type, floor, n) => {
  if (type === "boss") {
    const boss = getBossForFloor(floor);
    const scaled = coopScale(scaleEnemyStats(boss.baseStats, floor), n);
    return { ...boss, stats: scaled, isBoss: true };
  }
  if (type === "elite") {
    const e = getRandomEnemy(true);
    const scaled = coopScale(scaleEnemyStats(e.baseStats, floor + 1), n);
    return { ...e, stats: scaled, isElite: true };
  }
  const e = getRandomEnemy(false);
  const scaled = coopScale(scaleEnemyStats(e.baseStats, floor), n);
  return { ...e, stats: scaled };
};

const CoopMap = () => {
  const { lobby, coopMode, publishState, sendPlayerPos, remotePlayersRef, leaveLobby } = useCoop();
  const { user, loginWithGoogle } = useAuth();
  const { unlockAchievement } = useProfile();
  const navigate = useNavigate();
  const myId = user?.user_id;

  const state = lobby?.game_state;
  const rev = lobby?.state_revision;
  const grid = state?.grid;
  const floor = state?.floor || 1;
  const myPlayer = state?.players?.[myId] || null;
  const combat = state?.combat || null;
  const duel = state?.duel || null;
  const inMyDuel = !!(duel && (duel.a === myId || duel.b === myId));
  const players = state?.players || {};

  const [localEnc, setLocalEnc] = useState(null); // {type, data...} per-player overlay
  const [tick, setTick] = useState(0); // force re-render for HUD
  const [showStats, setShowStats] = useState(false); // coop stats/inventory modal
  const [spectateInfo, setSpectateInfo] = useState(null); // {name} when spectating (dead)
  const dismissedRoomsRef = useRef(new Set()); // rooms whose overlay was closed without completing

  // Optimistic room key (instant feel on door cross)
  const pendingRoomKeyRef = useRef(null);
  const sharedRoomKey = myPlayer?.roomKey || grid?.startKey || null;
  const myRoomKey = pendingRoomKeyRef.current || sharedRoomKey;
  useEffect(() => {
    if (pendingRoomKeyRef.current && sharedRoomKey === pendingRoomKeyRef.current) {
      pendingRoomKeyRef.current = null;
    }
  }, [sharedRoomKey]);

  const myRoom = grid?.rooms?.[myRoomKey] || null;
  const myAlive = !!(myPlayer?.alive && (myPlayer?.character?.stats?.pv || 0) > 0);
  const roomCleared = myRoom ? isRoomClearedFor(myRoom, myId) : true;

  // Canvas refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const keysRef = useRef({});
  const playerPosRef = useRef({ wx: ROOM_W / 2, wy: ROOM_H - 1.5, facing: "up", moving: false });
  const trailRef = useRef([]);
  const spritesRef = useRef({});
  const lastTickRef = useRef(0);
  const animRef = useRef(null);
  const interactCdRef = useRef(0);
  const posSendCdRef = useRef(0);
  const stepCdRef = useRef(0);
  const fDuelCdRef = useRef(0);
  const remoteSmoothRef = useRef({});
  const sawAllyThisFloorRef = useRef(false);
  const spectateRef = useRef({ idx: 0 });
  const spectateCdRef = useRef(0);
  const spectateNameRef = useRef(null);

  // Latest values for the render loop closure
  const liveRef = useRef({});
  liveRef.current = { state, rev, grid, myRoomKey, myRoom, roomCleared, myAlive, combat, duel, players, myId, floor };

  const publish = useCallback((producer) => {
    const cur = liveRef.current.state;
    if (!cur) return false;
    const next = producer(structuredClone(cur));
    if (!next) return false;
    return publishState(next, { expectedRevision: liveRef.current.rev });
  }, [publishState]);

  // ===== Redirects =====
  useEffect(() => {
    if (!user) return;
    if (!coopMode || !state) {
      navigate("/lobby");
    } else if (state && !state.grid) {
      // Legacy/invalid game state (no open grid) → back to lobby
      navigate("/lobby");
    }
  }, [user, coopMode, state, navigate]);

  // ===== Music =====
  useEffect(() => { playMusic(trackForFloor(floor)); }, [floor]);

  // ===== Load sprites (room interactables + all member classes) =====
  useEffect(() => {
    const wanted = [
      ["enemy_small", "/sprites/enemy_small.png"],
      ["enemy_elite", "/sprites/enemy_elite.png"],
      ["enemy_boss", "/sprites/enemy_boss.png"],
      ["chest", "/sprites/chest.png"],
      ["shop_npc", "/sprites/shop_npc.png"],
    ];
    const classes = Object.values(players).map((p) => p.classe_id).filter(Boolean);
    for (const cid of classes) {
      wanted.push([`player_${cid}`, `/sprites/${cid}.png`]);
      wanted.push([`player_${cid}_back`, `/sprites/back/${cid}.png`]);
      wanted.push([`player_${cid}_side`, `/sprites/side/${cid}.png`]);
    }
    let cancelled = false;
    (async () => {
      for (const [key, src] of wanted) {
        if (spritesRef.current[key]) continue;
        try {
          const c = await loadChromaKeySprite(src);
          if (cancelled) return;
          spritesRef.current[key] = c;
        } catch (e) { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [players]);

  // ===== Reset spawn on room change =====
  useEffect(() => {
    playerPosRef.current = { wx: ROOM_W / 2, wy: ROOM_H - 1.5, facing: "up", moving: false };
    interactCdRef.current = performance.now() + 600;
    dismissedRoomsRef.current.delete(myRoomKey); // re-entering a room re-enables its overlay
  }, [myRoomKey]);

  // ===== Keyboard =====
  useEffect(() => {
    const blocked = new Set(["z", "q", "s", "d", "w", "a", "f", "arrowup", "arrowdown", "arrowleft", "arrowright"]);
    const down = (e) => { const k = e.key.toLowerCase(); keysRef.current[k] = true; if (blocked.has(k)) e.preventDefault(); };
    const up = (e) => { keysRef.current[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // ===== Doors of my current room =====
  const myDoors = useMemo(() => {
    if (!grid || !myRoomKey) return [];
    return doorsForRoom(grid, myRoomKey).map((d) => ({
      dir: d.dir,
      targetKey: d.targetKey,
      pos: DOOR_POS[d.dir],
      targetType: grid.rooms[d.targetKey]?.type,
    }));
  }, [grid, myRoomKey]);
  const myDoorsRef = useRef(myDoors);
  myDoorsRef.current = myDoors;

  // ===== Actions =====
  const enterRoom = useCallback((targetKey) => {
    if (interactCdRef.current > performance.now()) return;
    interactCdRef.current = performance.now() + 700;
    SFX.door?.();
    pendingRoomKeyRef.current = targetKey;
    setTick((t) => t + 1);
    publish((s) => {
      if (!s.players?.[myId]) return null;
      s.players[myId].roomKey = targetKey;
      return s;
    });
  }, [publish, myId]);

  const triggerCombat = useCallback(() => {
    if (interactCdRef.current > performance.now()) return;
    interactCdRef.current = performance.now() + 800;
    SFX.click?.();
    publish((s) => {
      const room = s.grid?.rooms?.[liveRef.current.myRoomKey];
      if (!room || room.combatCleared || s.combat) return null;
      const aliveUids = Object.keys(s.players).filter((uid) => s.players[uid]?.alive && (s.players[uid]?.character?.stats?.pv || 0) > 0);
      if (aliveUids.length === 0) return null;
      const enemy = buildEnemyForRoom(room.type, s.floor || 1, aliveUids.length);
      const energy = {};
      for (const uid of aliveUids) energy[uid] = 3;
      const extras = freshCombatExtras(s.players, aliveUids);
      s.combat = {
        enemy, enemyHp: enemy.stats.pv, enemyMaxHp: enemy.stats.pv, roomKey: room.key,
        turnOrder: aliveUids, turnIndex: 0, phase: "player", aiState: { cooldowns: {}, turnCount: 0 },
        energy, log: [`Un ${enemy.nom} surgit ! Toute l'équipe est appelée au combat.`],
        ...extras,
      };
      return s;
    });
  }, [publish]);

  const openLocalRoom = useCallback(() => {
    if (interactCdRef.current > performance.now()) return;
    interactCdRef.current = performance.now() + 600;
    const room = liveRef.current.myRoom;
    if (!room) return;
    if (room.type === "chest") {
      SFX.chest?.();
      const rarity = rollChestRarity(floor);
      setLocalEnc({ type: "chest", content: generateChestContent(rarity), rarity });
    } else if (room.type === "sanctuary") {
      SFX.click?.();
      const rarity = rollSanctuaryRarity(floor);
      setLocalEnc({ type: "sanctuary", rarity, options: [generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity), generateSanctuaryPassive(rarity)] });
    } else if (room.type === "shop") {
      SFX.click?.();
      setLocalEnc({ type: "shop", stock: generateShop(floor) });
    }
  }, [floor]);

  // mark current non-combat room cleared for me + apply a character mutation
  const finishLocalRoom = useCallback((mutate) => {
    publish((s) => {
      const room = s.grid?.rooms?.[liveRef.current.myRoomKey];
      if (room && !(room.clearedBy || []).includes(myId)) {
        room.clearedBy = [...(room.clearedBy || []), myId];
      }
      if (mutate && s.players?.[myId]?.character) {
        s.players[myId].character = mutate(s.players[myId].character);
      }
      return s;
    });
  }, [publish, myId]);

  const reviveAllDead = useCallback((priceToDeduct = 0) => {
    publish((s) => {
      let revived = 0;
      for (const uid of Object.keys(s.players)) {
        const pl = s.players[uid];
        if (!pl.alive || (pl.character?.stats?.pv || 0) <= 0) {
          pl.alive = true;
          const st = { ...pl.character.stats };
          st.pv = Math.max(1, Math.round(st.pvMax * 0.5));
          pl.character = { ...pl.character, stats: st };
          revived++;
        }
      }
      if (revived === 0) return null;
      if (priceToDeduct && s.players[myId]?.character) {
        s.players[myId].character.gold = Math.max(0, (s.players[myId].character.gold || 0) - priceToDeduct);
      }
      return s;
    });
  }, [publish, myId]);

  // ===== F-duel : lance un duel tour par tour (state partagé) =====
  const startDuel = useCallback((targetUid) => {
    const me = players[myId];
    const tgt = players[targetUid];
    if (!me?.alive || !tgt?.alive) return;
    publish((s) => {
      if (s.combat || s.duel) return null;
      const meS = s.players[myId];
      const tgtS = s.players[targetUid];
      if (!meS?.alive || !tgtS?.alive) return null;
      const meVit = meS.character.stats.vit || 0;
      const tgtVit = tgtS.character.stats.vit || 0;
      const order = meVit >= tgtVit ? [myId, targetUid] : [targetUid, myId];
      s.duel = {
        a: myId, b: targetUid,
        names: { [myId]: meS.name, [targetUid]: tgtS.name },
        classes: { [myId]: meS.classe_id, [targetUid]: tgtS.classe_id },
        hp: { [myId]: meS.character.stats.pv, [targetUid]: tgtS.character.stats.pv },
        maxHp: { [myId]: meS.character.stats.pvMax, [targetUid]: tgtS.character.stats.pvMax },
        energy: { [myId]: 3, [targetUid]: 3 },
        turnOrder: order, turnIndex: 0, round: 1,
        phase: "active", winner: null, loser: null,
        log: [`⚔️ ${meS.name} défie ${tgtS.name} en duel !`],
        ts: Date.now(),
      };
      return s;
    });
    SFX.click?.();
  }, [publish, myId, players]);

  // ===== Duel banner toast =====
  const lastBannerTs = useRef(0);
  useEffect(() => {
    const b = state?.duelBanner;
    if (b && b.ts && b.ts !== lastBannerTs.current) {
      lastBannerTs.current = b.ts;
      toast(`⚔️ Duel : ${b.winner} a vaincu ${b.loser} !`, { description: "Réanime le perdant avec un sérum en boutique.", duration: 4000 });
    }
  }, [state?.duelBanner]);

  // ===== Render loop =====
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    const resize = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const loop = (now) => {
      const dt = Math.min(0.05, (now - lastTickRef.current) / 1000 || 0);
      lastTickRef.current = now;
      const L = liveRef.current;
      const inCombat = !!L.combat;
      const inDuel = !!(L.duel && (L.duel.a === L.myId || L.duel.b === L.myId));
      const spectating = !L.myAlive && !inCombat && !inDuel;
      // Salle/joueurs à afficher : la mienne, ou celle d'un allié vivant qu'on observe quand on est KO
      let viewRoomKey = L.myRoomKey;
      let viewRoom = L.myRoom;
      let spectTargetUid = null;
      if (spectating) {
        const aliveAllies = Object.keys(L.players).filter((uid) => uid !== L.myId && L.players[uid]?.alive && (L.players[uid]?.character?.stats?.pv || 0) > 0);
        if (aliveAllies.length) {
          const idx = ((spectateRef.current.idx % aliveAllies.length) + aliveAllies.length) % aliveAllies.length;
          spectTargetUid = aliveAllies[idx];
          viewRoomKey = L.players[spectTargetUid]?.roomKey || L.myRoomKey;
          viewRoom = L.grid?.rooms?.[viewRoomKey] || L.myRoom;
          const nm = L.players[spectTargetUid]?.name || "Allié";
          if (nm !== spectateNameRef.current) { spectateNameRef.current = nm; setSpectateInfo({ name: nm }); }
        } else if (spectateNameRef.current !== null) { spectateNameRef.current = null; setSpectateInfo(null); }
      } else if (spectateNameRef.current !== null) { spectateNameRef.current = null; setSpectateInfo(null); }
      const room = viewRoom;
      const blockMove = inCombat || inDuel || !!localEncRef.current || !L.myAlive;

      const p = playerPosRef.current;
      if (!blockMove && room) {
        const keys = keysRef.current;
        let dx = 0, dy = 0;
        if (keys["z"] || keys["w"] || keys["arrowup"]) dy -= 1;
        if (keys["s"] || keys["arrowdown"]) dy += 1;
        if (keys["q"] || keys["a"] || keys["arrowleft"]) dx -= 1;
        if (keys["d"] || keys["arrowright"]) dx += 1;
        const len = Math.hypot(dx, dy);
        if (len > 0) {
          dx /= len; dy /= len;
          p.wx += dx * PLAYER_SPEED * dt;
          p.wy += dy * PLAYER_SPEED * dt;
          p.moving = true;
          if (Math.abs(dx) > Math.abs(dy)) p.facing = dx > 0 ? "right" : "left";
          else p.facing = dy > 0 ? "down" : "up";
          if (now > stepCdRef.current) { SFX.step?.(); stepCdRef.current = now + 280; }
          if (Math.random() < 0.6) {
            trailRef.current.push({ wx: p.wx - dx * 0.3, wy: p.wy - dy * 0.3, vx: -dx * 0.4 + (Math.random() - 0.5) * 0.3, vy: -dy * 0.4 + (Math.random() - 0.5) * 0.3, life: 0, maxLife: 0.6 + Math.random() * 0.3, size: 3 + Math.random() * 2, seed: Math.random() });
          }
          p.wx = Math.max(0.6, Math.min(ROOM_W - 0.6, p.wx));
          p.wy = Math.max(0.6, Math.min(ROOM_H - 0.6, p.wy));
        } else {
          p.moving = false;
        }
        const tr = trailRef.current;
        for (let i = tr.length - 1; i >= 0; i--) { const tp = tr[i]; tp.life += dt; tp.wx += tp.vx * dt; tp.wy += tp.vy * dt; if (tp.life >= tp.maxLife) tr.splice(i, 1); }

        // Interact at center if room not cleared (and not dismissed this visit)
        if (!L.roomCleared && !dismissedRoomsRef.current.has(L.myRoomKey)) {
          const d = Math.hypot(ROOM_W / 2 - p.wx, ROOM_H / 2 - p.wy);
          if (d < INTERACT_RADIUS) {
            if (isCombatRoom(room.type)) triggerCombat();
            else openLocalRoom();
          }
        }
        // Walk into a door (always available — open map)
        for (const door of myDoorsRef.current) {
          const d = Math.hypot(door.pos.wx - p.wx, door.pos.wy - p.wy);
          if (d < DOOR_RADIUS) { enterRoom(door.targetKey); break; }
        }
        // F-duel near an ally
        if (keys["f"] && now > fDuelCdRef.current) {
          const targets = L.remoteList || [];
          let nearest = null, nd = 999;
          for (const r of targets) {
            const dd = Math.hypot(r.pos.wx - p.wx, r.pos.wy - p.wy);
            if (dd < nd) { nd = dd; nearest = r; }
          }
          if (nearest && nd < F_DUEL_RADIUS) {
            fDuelCdRef.current = now + 2000;
            startDuel(nearest.uid);
          }
        }
        // Broadcast my position
        if (sendPlayerPos && now > posSendCdRef.current) {
          posSendCdRef.current = now + 80;
          sendPlayerPos({ wx: p.wx, wy: p.wy, facing: p.facing, moving: p.moving, nodeId: L.myRoomKey, classeId: L.players?.[L.myId]?.classe_id || null });
        }
      } else {
        p.moving = false;
        // Mode spectateur : touche A pour changer d'allié observé
        if (spectating) {
          const keys = keysRef.current;
          if (keys["a"] && now > spectateCdRef.current) {
            spectateCdRef.current = now + 350;
            spectateRef.current.idx += 1;
          }
        }
      }

      // ===== DRAW =====
      const W = canvas.clientWidth, H = canvas.clientHeight;
      ctx.clearRect(0, 0, W, H);
      const theme = themeForFloor(L.floor);
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#020617"); bg.addColorStop(1, "#0f172a");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      const roomType = room?.type || "combat";
      // Portes & état "nettoyé" pour la salle affichée (la mienne, ou celle observée)
      const viewDoors = (spectating && viewRoomKey !== L.myRoomKey && L.grid)
        ? doorsForRoom(L.grid, viewRoomKey).map((d) => ({ dir: d.dir, targetKey: d.targetKey, pos: DOOR_POS[d.dir], targetType: L.grid.rooms[d.targetKey]?.type }))
        : myDoorsRef.current;
      const viewCleared = (spectating && spectTargetUid)
        ? (viewRoom ? isRoomClearedFor(viewRoom, spectTargetUid) : true)
        : L.roomCleared;
      for (let ry = 0; ry < ROOM_H; ry++) for (let rx = 0; rx < ROOM_W; rx++) {
        const s = isoProject(rx + 0.5, ry + 0.5, W, H);
        drawBiomeTile(ctx, s.x, s.y, TILE_W, TILE_H, theme.id, rx, ry, (rx + ry) % 2 === 0);
      }
      drawWalls(ctx, W, H);
      drawTileDecor(ctx, ROOM_W, ROOM_H, W, H, now, theme.id, room?.key);
      drawBiomeDecor(ctx, W, H, now, theme.id);
      for (const door of viewDoors) drawDoor(ctx, door.pos, { type: door.targetType }, W, H, now, false);
      drawDecoration(ctx, roomType, W, H, now);

      // Build entity list (interactable + me + allies in same room)
      const entities = [];
      if (room && !viewCleared) entities.push({ kind: "interactable", node: { type: roomType }, depth: ROOM_W / 2 + ROOM_H / 2 });
      if (!spectating) entities.push({ kind: "player", depth: p.wx + p.wy });

      // Allies (same room as the view — my room, or the spectated ally's room)
      const remoteList = [];
      const targets = remotePlayersRef?.current || {};
      const smooth = remoteSmoothRef.current;
      const seen = new Set();
      for (const uid of Object.keys(targets)) {
        if (uid === L.myId) continue;
        const tg = targets[uid];
        const staleLimit = (spectating && uid === spectTargetUid) ? 30000 : 5000;
        if (!tg || Date.now() - (tg.lastUpdate || 0) > staleLimit) continue;
        // Same room: prefer shared roomKey, fallback to ephemeral nodeId
        const allyRoom = L.players?.[uid]?.roomKey;
        if (allyRoom && allyRoom !== viewRoomKey) continue;
        if (!allyRoom && tg.nodeId !== viewRoomKey) continue;
        seen.add(uid);
        if (!smooth[uid]) smooth[uid] = { wx: tg.wx, wy: tg.wy };
        smooth[uid].wx += (tg.wx - smooth[uid].wx) * Math.min(1, dt * 12);
        smooth[uid].wy += (tg.wy - smooth[uid].wy) * Math.min(1, dt * 12);
        smooth[uid].facing = tg.facing;
        smooth[uid].moving = tg.moving;
        smooth[uid].classeId = tg.classeId;
        smooth[uid].name = L.players?.[uid]?.name || "Allié";
        smooth[uid].dead = !(L.players?.[uid]?.alive && (L.players?.[uid]?.character?.stats?.pv || 0) > 0);
        remoteList.push({ uid, pos: smooth[uid] });
        entities.push({ kind: "remote", uid, pos: smooth[uid], depth: smooth[uid].wx + smooth[uid].wy });
      }
      for (const uid of Object.keys(smooth)) if (!seen.has(uid)) delete smooth[uid];
      L.remoteList = remoteList;
      if (remoteList.length > 0) sawAllyThisFloorRef.current = true;

      entities.sort((a, b) => a.depth - b.depth);
      for (const e of entities) {
        if (e.kind === "interactable") drawInteractable(ctx, e.node, spritesRef.current, W, H, now);
        else if (e.kind === "player") {
          const myClasse = L.players?.[L.myId]?.classe_id;
          drawPlayer(ctx, p, { front: spritesRef.current[`player_${myClasse}`], back: spritesRef.current[`player_${myClasse}_back`], side: spritesRef.current[`player_${myClasse}_side`] }, W, H, now, myClasse, trailRef.current);
        } else if (e.kind === "remote") {
          const rcid = e.pos.classeId;
          drawRemotePlayer(ctx, e.pos, { front: spritesRef.current[`player_${rcid}`], back: spritesRef.current[`player_${rcid}_back`], side: spritesRef.current[`player_${rcid}_side`] }, W, H, now, rcid, e.pos.name, e.pos.dead);
        }
      }

      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterRoom, triggerCombat, openLocalRoom, startDuel, sendPlayerPos]);

  // keep a ref of localEnc for the loop
  const localEncRef = useRef(localEnc);
  useEffect(() => { localEncRef.current = localEnc; }, [localEnc]);

  // Close any local overlay when a shared combat starts (everyone is pulled in)
  useEffect(() => { if (combat) setLocalEnc(null); }, [combat]);
  // Clear pending navigation when floor changes (boss → fresh floor resets everyone)
  useEffect(() => {
    // Succès "Loup solitaire" : étage coop terminé sans croiser un seul allié
    if (floor > 1 && !sawAllyThisFloorRef.current) unlockAchievement?.("lone_wolf");
    sawAllyThisFloorRef.current = false;
    pendingRoomKeyRef.current = null;
  }, [floor, unlockAchievement]);
  // Retry roomKey publish if a CAS conflict dropped it (converge toward shared state)
  useEffect(() => {
    const target = pendingRoomKeyRef.current;
    if (!target || target === sharedRoomKey) return;
    const t = setTimeout(() => {
      if (pendingRoomKeyRef.current && pendingRoomKeyRef.current !== sharedRoomKey) {
        publish((s) => { if (!s.players?.[myId]) return null; s.players[myId].roomKey = pendingRoomKeyRef.current; return s; });
      }
    }, 220);
    return () => clearTimeout(t);
  }, [sharedRoomKey, rev, floor, publish, myId]);

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white text-center p-4">
        <div>
          <p className="mb-3">Connecte-toi pour jouer en co-op.</p>
          <button data-testid="coop-login-btn" onClick={loginWithGoogle} className="bg-white text-slate-950 px-3 py-2 rounded-lg font-bold">Connexion</button>
        </div>
      </div>
    );
  }
  if (!state || !grid) {
    return <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white">Chargement de l'aventure…</div>;
  }

  const theme = themeForFloor(floor);
  const deadAllies = Object.values(players).filter((p) => !p.alive || (p.character?.stats?.pv || 0) <= 0);

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden relative bg-slate-950" data-testid="coop-map" data-rev={tick}>
      {/* Top bar */}
      <div className="px-4 py-2 bg-slate-800 border-b-4 border-slate-950 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span data-testid="coop-floor" className="font-display font-bold text-fuchsia-300 text-sm">Étage {floor}</span>
          <span className="text-xs text-slate-300 bg-slate-900/70 border-2 border-slate-950 rounded px-2 py-0.5">{theme.accent} {theme.name}</span>
          {myRoom && (
            <span data-testid="coop-room-type" className="text-xs text-slate-400">Salle : {myRoom.type}{roomCleared ? " ✓" : ""}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {myPlayer?.character && (
            <span data-testid="coop-my-hp" className="text-xs text-emerald-300 font-bold">❤️ {Math.max(0, myPlayer.character.stats.pv)}/{myPlayer.character.stats.pvMax}</span>
          )}
          {myPlayer?.character && (
            <button data-testid="coop-stats-btn" onClick={() => { SFX.click?.(); setShowStats(true); }}
              className="bg-sky-500 text-white border-4 border-slate-950 rounded-xl px-3 py-1 cartoon-shadow text-xs font-bold flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> Stats
            </button>
          )}
          <button data-testid="coop-leave-btn" onClick={() => { if (window.confirm("Quitter la partie coop ?")) { leaveLobby().then(() => navigate("/")); } }}
            className="bg-rose-500 text-white border-4 border-slate-950 rounded-xl px-3 py-1 cartoon-shadow text-xs font-bold flex items-center gap-1">
            <LogOut className="w-3 h-3" /> Quitter
          </button>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        <canvas ref={canvasRef} data-testid="coop-canvas" />
        {!combat && myAlive && !localEnc && (
          <div className="absolute bottom-3 left-3 bg-slate-900/80 text-slate-100 border-2 border-slate-700 rounded-lg px-3 py-2 text-xs font-mono pointer-events-none">
            <p className="font-bold text-yellow-300 mb-1">Déplacement libre</p>
            <p>ZQSD / WASD / Flèches</p>
            <p className="text-fuchsia-300 mt-1">Touche F près d'un allié = duel tour par tour ⚔️</p>
          </div>
        )}
        {!myAlive && !combat && (
          spectateInfo ? (
            <div data-testid="coop-spectate-hud" className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-slate-900/85 text-slate-100 border-2 border-rose-500 rounded-xl px-4 py-2 text-center pointer-events-none z-20">
              <p className="font-display font-bold text-rose-400 text-sm flex items-center justify-center gap-1"><Skull className="w-4 h-4" /> Tu es KO — Mode spectateur</p>
              <p className="text-xs text-slate-200 mt-0.5">Tu observes <b className="text-sky-300" data-testid="coop-spectate-name">{spectateInfo.name}</b> · <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Appuie sur <span className="px-1 rounded bg-slate-800 border border-slate-600 font-mono">A</span> pour changer</span></p>
              <p className="text-[11px] text-emerald-300 mt-0.5">Un allié peut te ranimer avec un Sérum en boutique.</p>
            </div>
          ) : (
            <div data-testid="coop-dead-overlay" className="absolute inset-0 flex items-center justify-center bg-slate-950/70 z-20">
              <div className="text-center">
                <Skull className="w-12 h-12 text-rose-500 mx-auto mb-2" />
                <p className="font-display text-xl font-bold text-white">Tu es KO</p>
                <p className="text-slate-300 text-sm">Un allié doit acheter un <b className="text-emerald-300">Sérum de Résurrection</b> en boutique pour te ranimer.</p>
              </div>
            </div>
          )
        )}
      </div>

      {/* Shared team combat */}
      {combat && <CoopCombat />}

      {/* Turn-based F-duel between two coop players */}
      {inMyDuel && <CoopDuel />}

      {/* Local per-player overlays */}
      {localEnc?.type === "chest" && (
        <CoopChestOverlay enc={localEnc} onTake={() => {
          const content = localEnc.content;
          finishLocalRoom((c) => {
            let u = applyChestBonuses(c, content.stats);
            u.gold = (u.gold || 0) + content.gold;
            const rs = { ...(u.runStats || {}) };
            rs.goldEarned = (rs.goldEarned || 0) + (content.gold || 0);
            u.runStats = rs;
            return u;
          });
          toast.success(`Coffre ouvert : +${localEnc.content.gold} or`);
          setLocalEnc(null);
        }} onClose={() => { dismissedRoomsRef.current.add(myRoomKey); setLocalEnc(null); }} />
      )}
      {localEnc?.type === "sanctuary" && (
        <CoopSanctuaryOverlay enc={localEnc} onPick={(passive) => {
          finishLocalRoom((c) => {
            const stats = { ...c.stats };
            if (passive.stat && passive.value && passive.stat !== "heal") {
              stats[passive.stat] = (stats[passive.stat] || 0) + passive.value;
              if (passive.stat === "pv") stats.pvMax = (stats.pvMax || stats.pv) + passive.value;
            }
            return { ...c, stats, passifs: [...(c.passifs || []), passive] };
          });
          toast.success(`Passif obtenu : ${passive.nom}`);
          setLocalEnc(null);
        }} />
      )}
      {localEnc?.type === "shop" && (
        <CoopShopOverlay enc={localEnc} character={myPlayer?.character} deadCount={deadAllies.length}
          onBuyPotion={(potion) => {
            if ((myPlayer?.character?.gold || 0) < potion.price) { toast.error("Pas assez d'or"); return; }
            publish((s) => {
              const c = s.players[myId].character;
              const stats = { ...c.stats };
              c.gold -= potion.price;
              if (potion.stat === "pv") { stats.pvMax += potion.value; stats.pv += potion.value; }
              else stats[potion.stat] = (stats[potion.stat] || 0) + potion.value;
              s.players[myId].character = { ...c, stats };
              const sc = s.grid.rooms[liveRef.current.myRoomKey];
              if (sc) sc.clearedBy = Array.from(new Set([...(sc.clearedBy || []), myId]));
              return s;
            });
            setLocalEnc((e) => ({ ...e, stock: { ...e.stock, potions: e.stock.potions.map((p) => p.id === potion.id ? { ...p, sold: true } : p) } }));
            toast.success(`Acheté : ${potion.nom}`);
          }}
          onBuySerum={(price) => {
            if ((myPlayer?.character?.gold || 0) < price) { toast.error("Pas assez d'or"); return; }
            reviveAllDead(price);
            setLocalEnc((e) => ({ ...e, serumSold: true }));
            unlockAchievement?.("medic");
            toast.success("Sérum utilisé : alliés réanimés à 50% PV !");
          }}
          onClose={() => { dismissedRoomsRef.current.add(myRoomKey); setLocalEnc(null); }} />
      )}

      {/* Stats & inventory modal (comme en solo) */}
      {showStats && myPlayer?.character && (
        <CoopStatsModal character={myPlayer.character} classe={CLASSES[myPlayer.classe_id]} onClose={() => setShowStats(false)} />
      )}
    </div>
  );
};

// ===== Local overlay components =====
function CoopChestOverlay({ enc, onTake, onClose }) {
  return (
    <div data-testid="coop-chest-overlay" className="absolute inset-0 z-30">
      <ChestRewardView content={{ ...enc.content, rarity: enc.content.rarity || enc.rarity }} onClaim={onTake} onClose={onClose} />
    </div>
  );
}

function CoopSanctuaryOverlay({ enc, onPick }) {
  return (
    <div data-testid="coop-sanctuary-overlay" className="absolute inset-0 z-30">
      <SanctuaryChoiceView rarity={enc.rarity} options={enc.options} onSelect={onPick} />
    </div>
  );
}

function CoopStatsModal({ character, classe, onClose }) {
  const inv = character.inventory || [];
  return (
    <div data-testid="coop-stats-modal" className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static max-w-2xl w-full relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button data-testid="coop-stats-close" onClick={onClose}
          className="absolute -top-2 -right-2 bg-rose-500 text-white border-4 border-slate-950 rounded-full w-9 h-9 flex items-center justify-center cartoon-shadow z-10">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          {classe && (
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${classe.couleur} border-2 border-slate-950 overflow-hidden flex-shrink-0`}>
              <img src={classe.image} alt={classe.nom} className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <h2 className="font-display text-2xl font-bold">{classe?.nom || "Mon personnage"}</h2>
            <p className="text-xs text-slate-400 uppercase tracking-wider"><span className="text-yellow-300">🪙 {character.gold || 0} or</span></p>
          </div>
        </div>

        <h3 className="font-display text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> Stats actuelles</h3>
        <div className="mb-5">
          <StatsGrid stats={character.stats} compact data-testid="coop-stats-grid" />
        </div>

        <h3 className="font-display text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><Backpack className="w-3.5 h-3.5" /> Inventaire ({inv.length})</h3>
        {inv.length === 0 ? (
          <p className="text-center text-slate-400 py-4 text-sm">Aucun objet pour l'instant. Trouve-en dans les coffres et boutiques !</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {inv.map((entry) => {
              const item = ITEMS[entry.id];
              if (!item) return null;
              const rarityBorder = ITEM_RARITY_COLORS[item.rarity] || ITEM_RARITY_COLORS.common;
              return (
                <div key={entry.uuid} data-testid={`coop-inv-item-${item.id}`} className={`bg-slate-900 border-4 ${rarityBorder} rounded-2xl p-3`}>
                  <div className={`${item.color} border-2 border-slate-950 rounded-xl h-14 flex items-center justify-center text-2xl mb-2`}>{item.icon}</div>
                  <h4 className="font-display font-bold text-sm leading-tight">{item.nom}</h4>
                  <p className="text-[11px] text-slate-300 leading-tight">{item.description}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CoopShopOverlay({ enc, character, deadCount, onBuyPotion, onBuySerum, onClose }) {
  const gold = character?.gold || 0;
  const serumPrice = 120;
  return (
    <div data-testid="coop-shop-overlay" className="absolute inset-0 z-30 bg-slate-950/90 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 max-w-2xl w-full cartoon-shadow-lg-static">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-bold text-yellow-300">💰 Boutique Coop</h2>
          <span data-testid="coop-shop-gold" className="text-yellow-300 font-bold">🪙 {gold} or</span>
        </div>

        {/* Resurrection serum — always available in coop */}
        <div data-testid="coop-serum" className="bg-emerald-900/40 border-2 border-emerald-500 rounded-2xl p-3 mb-3 flex items-center justify-between">
          <div>
            <p className="font-display font-bold text-emerald-300">🧪 Sérum de Résurrection</p>
            <p className="text-xs text-slate-300">Réanime tous les alliés KO à 50% PV. {deadCount > 0 ? `(${deadCount} KO)` : "(aucun KO actuellement)"}</p>
          </div>
          <button data-testid="coop-buy-serum" onClick={() => onBuySerum(serumPrice)} disabled={enc.serumSold || deadCount === 0 || gold < serumPrice}
            className="bg-emerald-500 text-slate-950 font-bold px-3 py-2 rounded-xl border-4 border-slate-950 disabled:opacity-40">
            {enc.serumSold ? "Utilisé" : `${serumPrice} or`}
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          {enc.stock.potions.map((p) => (
            <div key={p.id} className="bg-slate-900 border-2 border-slate-700 rounded-xl p-2 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold">{p.icon} {p.nom}</p>
                <p className="text-[11px] text-slate-400">{p.description}</p>
              </div>
              <button data-testid={`coop-buy-potion-${p.key}`} onClick={() => onBuyPotion(p)} disabled={p.sold || gold < p.price}
                className="bg-yellow-400 text-slate-950 font-bold px-2 py-1 rounded-lg border-2 border-slate-950 text-xs disabled:opacity-40">
                {p.sold ? "Vendu" : `${p.price} or`}
              </button>
            </div>
          ))}
        </div>
        <div className="text-center mt-4">
          <button data-testid="coop-shop-close" onClick={onClose} className="bg-slate-700 text-white font-bold px-4 py-2 rounded-xl border-4 border-slate-950">Quitter la boutique</button>
        </div>
      </div>
    </div>
  );
}

export default CoopMap;
